import * as clack from "@clack/prompts";
import * as pulumi from "@pulumi/pulumi";
import pc from "picocolors";
import { deployEmailStack } from "../../infrastructure/email-stack.js";
import { trackCommand, trackError } from "../../telemetry/events.js";
import type { EmailConfigOptions } from "../../types/index.js";
import { resolveDashboardUrl } from "../../utils/selfhost/dashboard-url.js";
import {
  getAWSRegion,
  validateAWSCredentials,
} from "../../utils/shared/aws.js";
import { errors } from "../../utils/shared/errors.js";
import {
  ensurePulumiWorkDir,
  getPulumiWorkDir,
} from "../../utils/shared/fs.js";
import { isJsonMode, jsonSuccess } from "../../utils/shared/json-output.js";
import {
  buildEmailStackConfig,
  loadConnectionMetadata,
  saveConnectionMetadata,
} from "../../utils/shared/metadata.js";
import {
  DeploymentProgress,
  displayPreview,
  displaySuccess,
} from "../../utils/shared/output.js";
import {
  ensurePulumiInstalled,
  previewWithResourceChanges,
} from "../../utils/shared/pulumi.js";

/**
 * Config command - Redeploy infrastructure to apply CLI updates
 * This command updates Lambda functions and other managed resources
 * without requiring configuration changes from the user.
 */
export async function config(options: EmailConfigOptions): Promise<void> {
  const startTime = Date.now();

  if (!isJsonMode()) {
    clack.intro(
      pc.bold(
        options.preview
          ? "Wraps Config Preview"
          : "Wraps Config - Apply CLI Updates to Infrastructure"
      )
    );
  }

  const progress = new DeploymentProgress();

  // 1. Check Pulumi CLI is installed
  const wasAutoInstalled = await progress.execute(
    "Checking Pulumi CLI installation",
    async () => await ensurePulumiInstalled()
  );

  if (wasAutoInstalled) {
    progress.info("Pulumi CLI was automatically installed");
  }

  // 2. Validate AWS credentials
  const identity = await progress.execute(
    "Validating AWS credentials",
    async () => validateAWSCredentials()
  );

  progress.info(`Connected to AWS account: ${pc.cyan(identity.accountId)}`);

  // 3. Get region
  let region = options.region;
  if (!region) {
    const defaultRegion = await getAWSRegion();
    region = defaultRegion;
  }

  // 4. Load existing connection metadata
  const metadata = await loadConnectionMetadata(identity.accountId, region);

  if (!metadata) {
    clack.log.error(
      `No Wraps connection found for account ${pc.cyan(identity.accountId)} in region ${pc.cyan(region)}`
    );
    clack.log.info(
      `Use ${pc.cyan("wraps email init")} to create new infrastructure or ${pc.cyan("wraps email connect")} to connect existing.`
    );
    process.exit(1);
  }

  progress.info(`Found existing connection created: ${metadata.timestamp}`);

  // 5. Display current configuration
  console.log(`\n${pc.bold("Current Configuration:")}\n`);

  if (metadata.services.email?.preset) {
    console.log(`  Preset: ${pc.cyan(metadata.services.email?.preset)}`);
  } else {
    console.log(`  Preset: ${pc.cyan("custom")}`);
  }

  const config = metadata.services.email?.config;

  if (!config) {
    clack.log.error("No email configuration found in metadata");
    clack.log.info(
      `Use ${pc.cyan("wraps email init")} to create new infrastructure.`
    );
    process.exit(1);
  }

  // Show sending domain if configured
  if (config.domain) {
    console.log(`  Sending Domain: ${pc.cyan(config.domain)}`);
  }

  if (config.tracking?.enabled) {
    console.log(`  ${pc.green("✓")} Open & Click Tracking`);
  }

  if (config.suppressionList?.enabled) {
    console.log(`  ${pc.green("✓")} Bounce/Complaint Suppression`);
  }

  if (config.eventTracking?.enabled) {
    console.log(`  ${pc.green("✓")} Event Tracking (EventBridge)`);
  }

  if (config.dedicatedIp) {
    console.log(`  ${pc.green("✓")} Dedicated IP Address`);
  }

  console.log("");

  // 6. Show what will be updated
  console.log(`${pc.bold("What will be updated:")}\n`);
  console.log(
    `  ${pc.cyan("•")} Lambda function code (if event tracking enabled)`
  );
  console.log(
    `  ${pc.cyan("•")} EventBridge rules (if event tracking enabled)`
  );
  console.log(`  ${pc.cyan("•")} IAM policies (security improvements)`);
  console.log(`  ${pc.cyan("•")} SES configuration set (feature updates)`);
  console.log("");

  progress.info(
    "Your current configuration will be preserved - no features will be added or removed"
  );
  console.log("");

  // 7. Confirm update (skip if --yes or --preview)
  if (!(options.yes || options.preview)) {
    const confirmed = await clack.confirm({
      message: "Proceed with update?",
      initialValue: true,
    });

    if (clack.isCancel(confirmed) || !confirmed) {
      clack.cancel("Update cancelled.");
      process.exit(0);
    }
  }

  // 8. Build stack configuration from metadata (preserves all existing config including webhook)
  const stackConfig = buildEmailStackConfig(metadata, region);

  // 10. Preview or Update Pulumi stack
  if (options.preview) {
    // PREVIEW MODE - show what would be updated without deploying
    try {
      const previewResult = await progress.execute(
        "Generating update preview",
        async () => {
          await ensurePulumiWorkDir({ accountId: identity.accountId, region });

          const stack =
            await pulumi.automation.LocalWorkspace.createOrSelectStack(
              {
                stackName:
                  metadata.services.email?.pulumiStackName ||
                  `wraps-${identity.accountId}-${region}`,
                projectName: "wraps-email",
                program: async () => {
                  const result = await deployEmailStack(stackConfig);
                  return {
                    roleArn: result.roleArn,
                    configSetName: result.configSetName,
                    tableName: result.tableName,
                    region: result.region,
                    lambdaFunctions: result.lambdaFunctions,
                    domain: result.domain,
                    dkimTokens: result.dkimTokens,
                    customTrackingDomain: result.customTrackingDomain,
                  };
                },
              },
              {
                workDir: getPulumiWorkDir(),
                envVars: {
                  PULUMI_CONFIG_PASSPHRASE: "",
                  AWS_REGION: region,
                },
                secretsProvider: "passphrase",
              }
            );

          await stack.setConfig("aws:region", { value: region });

          // Refresh state to sync with AWS before previewing
          await stack.refresh({ onOutput: () => {} });

          // Run preview with resource change capture
          const result = await previewWithResourceChanges(stack, {
            diff: true,
          });
          return result;
        }
      );

      // Display preview results with detailed resource changes
      displayPreview({
        changeSummary: previewResult.changeSummary,
        resourceChanges: previewResult.resourceChanges,
        commandName: "wraps email config",
      });

      clack.outro(
        pc.green("Preview complete. Run without --preview to update.")
      );

      // Track preview completion
      trackCommand("email:config", {
        success: true,
        preview: true,
        duration_ms: Date.now() - startTime,
      });
      return;
    } catch (error) {
      trackError("PREVIEW_FAILED", "email:config", { step: "preview" });
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes("stack is currently locked")) {
        throw errors.stackLocked();
      }
      throw new Error(`Preview failed: ${msg}`);
    }
  }

  // UPDATE MODE - actually update infrastructure
  let outputs;
  try {
    outputs = await progress.execute(
      "Updating Wraps infrastructure (this may take 2-3 minutes)",
      async () => {
        await ensurePulumiWorkDir({ accountId: identity.accountId, region });

        const stack =
          await pulumi.automation.LocalWorkspace.createOrSelectStack(
            {
              stackName:
                metadata.services.email?.pulumiStackName ||
                `wraps-${identity.accountId}-${region}`,
              projectName: "wraps-email",
              program: async () => {
                const result = await deployEmailStack(stackConfig);

                return {
                  roleArn: result.roleArn,
                  configSetName: result.configSetName,
                  tableName: result.tableName,
                  region: result.region,
                  lambdaFunctions: result.lambdaFunctions,
                  domain: result.domain,
                  dkimTokens: result.dkimTokens,
                  customTrackingDomain: result.customTrackingDomain,
                };
              },
            },
            {
              workDir: getPulumiWorkDir(),
              envVars: {
                PULUMI_CONFIG_PASSPHRASE: "",
                AWS_REGION: region,
              },
              secretsProvider: "passphrase",
            }
          );

        await stack.workspace.selectStack(
          metadata.services.email?.pulumiStackName ||
            `wraps-${identity.accountId}-${region}`
        );
        await stack.setConfig("aws:region", { value: region });

        // Refresh state to sync with actual AWS resources (prevents AlreadyExists errors)
        await stack.refresh({ onOutput: () => {} });

        // Skip import flags — the stack already exists so resources are tracked in state
        stackConfig.skipResourceImports = true;

        // Pulumi will automatically detect changes and only update what's needed
        const upResult = await stack.up({ onOutput: () => {} });
        const pulumiOutputs = upResult.outputs;

        return {
          roleArn: pulumiOutputs.roleArn?.value as string,
          configSetName: pulumiOutputs.configSetName?.value as
            | string
            | undefined,
          tableName: pulumiOutputs.tableName?.value as string | undefined,
          region: pulumiOutputs.region?.value as string,
          lambdaFunctions: pulumiOutputs.lambdaFunctions?.value as
            | string[]
            | undefined,
          domain: pulumiOutputs.domain?.value as string | undefined,
          dkimTokens: pulumiOutputs.dkimTokens?.value as string[] | undefined,
          customTrackingDomain: pulumiOutputs.customTrackingDomain?.value as
            | string
            | undefined,
        };
      }
    );
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    // Track update failure
    trackCommand("email:config", {
      success: false,
      duration_ms: Date.now() - startTime,
    });

    // Check if it's a lock file error
    if (msg.includes("stack is currently locked")) {
      trackError("STACK_LOCKED", "email:config", { step: "update" });
      throw errors.stackLocked();
    }

    trackError("UPDATE_FAILED", "email:config", { step: "update" });
    throw new Error(`Pulumi update failed: ${msg}`);
  }

  // 11. Update metadata timestamp (config stays the same)
  metadata.timestamp = new Date().toISOString();
  await saveConnectionMetadata(metadata);

  progress.info("Connection metadata updated");

  // 12. Display success message
  if (isJsonMode()) {
    jsonSuccess("email.config", {
      updated: true,
      region: outputs.region!,
      roleArn: outputs.roleArn,
      configSetName: outputs.configSetName,
    });
    trackCommand("email:config", {
      success: true,
      duration_ms: Date.now() - startTime,
    });
    return;
  }

  displaySuccess({
    dashboardUrl: await resolveDashboardUrl(
      identity.accountId,
      outputs.region!
    ),
    roleArn: outputs.roleArn,
    configSetName: outputs.configSetName,
    region: outputs.region!,
    tableName: outputs.tableName,
    customTrackingDomain: outputs.customTrackingDomain,
  });

  // Show what was updated
  console.log(`\n${pc.green("✓")} ${pc.bold("Update complete!")}\n`);
  console.log(
    "Infrastructure has been updated with the latest CLI improvements.\n"
  );
  console.log(`${pc.bold("Next steps:")}\n`);
  console.log(
    `  ${pc.cyan("1.")} No code changes needed - your existing SDK integration continues to work`
  );
  console.log(
    `  ${pc.cyan("2.")} Check ${pc.cyan("wraps status")} to verify all resources are healthy`
  );
  console.log(
    `  ${pc.cyan("3.")} View analytics at ${pc.cyan("wraps console")}\n`
  );

  // 13. Track successful update
  trackCommand("email:config", {
    success: true,
    duration_ms: Date.now() - startTime,
  });
}
